import { Capacitor } from "@capacitor/core";

/**
 * Impact style options for haptic feedback.
 * Matches Capacitor's ImpactStyle enum.
 */
export type HapticsImpactStyle = "Heavy" | "Medium" | "Light";

/**
 * Triggers a haptic impact feedback.
 * On native platforms, uses the device's haptic engine.
 * On web, this is a no-op.
 *
 * @param style - The intensity of the impact (Heavy, Medium, or Light)
 */
export async function hapticsImpact(style: HapticsImpactStyle = "Medium"): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  const { Haptics, ImpactStyle } = await import("@capacitor/haptics");
  const impactStyle = ImpactStyle[style];
  await Haptics.impact({ style: impactStyle });
}

/**
 * Triggers a haptic notification feedback.
 * On native platforms, uses the device's haptic engine.
 * On web, this is a no-op.
 *
 * @param type - The type of notification (Success, Warning, or Error)
 */
export async function hapticsNotification(
  type: "Success" | "Warning" | "Error" = "Success"
): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  const { Haptics, NotificationType } = await import("@capacitor/haptics");
  const notificationType = NotificationType[type];
  await Haptics.notification({ type: notificationType });
}

/**
 * Triggers a haptic vibration.
 * On native platforms, uses the device's haptic engine.
 * On web, this is a no-op.
 *
 * @param duration - Duration of vibration in milliseconds (default: 300)
 */
export async function hapticsVibrate(duration = 300): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  const { Haptics } = await import("@capacitor/haptics");
  await Haptics.vibrate({ duration });
}

/**
 * Starts a selection gesture haptic feedback.
 * Call selectionEnd() when the selection gesture ends.
 * On native platforms, uses the device's haptic engine.
 * On web, this is a no-op.
 */
export async function hapticsSelectionStart(): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  const { Haptics } = await import("@capacitor/haptics");
  await Haptics.selectionStart();
}

/**
 * Provides haptic feedback during a selection gesture.
 * Should be called between selectionStart() and selectionEnd().
 * On native platforms, uses the device's haptic engine.
 * On web, this is a no-op.
 */
export async function hapticsSelectionChanged(): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  const { Haptics } = await import("@capacitor/haptics");
  await Haptics.selectionChanged();
}

/**
 * Ends a selection gesture haptic feedback.
 * Call after selectionStart() when the selection gesture ends.
 * On native platforms, uses the device's haptic engine.
 * On web, this is a no-op.
 */
export async function hapticsSelectionEnd(): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  const { Haptics } = await import("@capacitor/haptics");
  await Haptics.selectionEnd();
}
